var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import DebugService from './DebugService';
import LoggingService from './LoggingService';
import PlacementApiService from './PlacementApiService';
import { EmailConverterService } from './EmailConverterService';
import GraphEmailService from './GraphEmailService';
import OfficeModeService from './OfficeModeService';
import OfficeIdConverterService from './OfficeIdConverterService';
import { getSender, getSubject, getCreatedDate, detectSharedMailbox, getInternetMessageId } from '../utils/emailHelpers';
import { stampEmailWithWorkbenchId } from '../utils/emailStamping';
import { showWorkbenchNotificationBanner } from '../utils/outlookNotification';
import { environment } from '../config/environment';
export class WorkbenchService {
    // Environment variables
    get sharedMailbox() {
        return environment.CYBER_MRSNA_MAILBOX || environment.DEFAULT_SHARED_MAILBOX || '';
    }
    constructor() { }
    static getInstance() {
        if (!WorkbenchService.instance) {
            WorkbenchService.instance = new WorkbenchService();
        }
        return WorkbenchService.instance;
    }
    submitPlacement(apiToken, graphToken, item, 
    //productCode: string,
    lineOfBusiness, subLineOfBusiness, sendCopyToCyberAdmin, assignIngestionToSelf) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.section('Starting Placement Submission');
                DebugService.object('Submission parameters', { lineOfBusiness, subLineOfBusiness, sendCopyToCyberAdmin });
                // Continue with normal submission flow
                return yield this.processSubmission(apiToken, graphToken, item, lineOfBusiness, subLineOfBusiness, sendCopyToCyberAdmin, assignIngestionToSelf);
            }
            catch (error) {
                const errObj = error instanceof Error ? error : new Error(String(error));
                yield LoggingService.logError(errObj, 'submitPlacement', {
                    user: Office.context.mailbox.userProfile.emailAddress
                });
                return { success: false, error: errObj.message };
            }
        });
    }
    retryForward(graphToken, placementId, graphItemId, sharedMailbox) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                DebugService.section('Retrying Email Forward');
                DebugService.object('Retry parameters', { placementId, graphItemId, sharedMailbox });
                // If we have a valid graphItemId, use the normal method
                if (graphItemId && graphItemId !== 'SEARCH_METHOD') {
                    yield GraphEmailService.forwardEmailWithGraphToken(graphToken, {
                        emailId: graphItemId,
                        uwwbID: placementId,
                        sharedMailbox: sharedMailbox,
                    });
                }
                else {
                    // For draft emails, try to get fresh itemId first, then conversationId, then search
                    const item = Office.context.mailbox.item;
                    let currentItemId = item.itemId;
                    // If no itemId and we're in draft mode, try to save and get it
                    if (!currentItemId && OfficeModeService.isComposeMode()) {
                        try {
                            DebugService.email('Getting fresh itemId for retry forwarding (draft mode)');
                            currentItemId = yield this.saveAndGetItemId(item);
                            DebugService.email('Got fresh itemId for retry');
                        }
                        catch (saveError) {
                            DebugService.warn('Failed to get fresh itemId, trying conversationId');
                        }
                    }
                    if (currentItemId) {
                        DebugService.email('Using fresh itemId for retry forwarding');
                        yield GraphEmailService.forwardEmailWithGraphToken(graphToken, {
                            emailId: currentItemId,
                            uwwbID: placementId,
                            sharedMailbox: sharedMailbox,
                        });
                    }
                    else {
                        // Try conversationId as fallback
                        const conversationId = item.conversationId;
                        if (conversationId) {
                            DebugService.email('Using conversationId for retry forwarding');
                            yield GraphEmailService.forwardEmailByConversationId(graphToken, conversationId, placementId, sharedMailbox);
                        }
                        else {
                            yield this.searchBySubjectAndDateThenForward(item, graphToken, placementId, sharedMailbox);
                        }
                    }
                }
                DebugService.email('Forwarding retry successful');
                return { success: true };
            }
            catch (error) {
                const errObj = error instanceof Error ? error : new Error(String(error));
                yield LoggingService.logError(errObj, 'retryForward', {
                    user: Office.context.mailbox.userProfile.emailAddress
                });
                return {
                    success: false,
                    error: errObj.message,
                    forwardingFailed: true,
                    forwardingFailedReason: errObj.message
                };
            }
        });
    }
    searchBySubjectAndDateThenForward(item, graphToken, placementId, sharedMailbox) {
        return __awaiter(this, void 0, void 0, function* () {
            DebugService.debug('Using search fallback for retry with subject and createdDate');
            try {
                const emailSubject = yield getSubject(item);
                const emailSender = yield getSender(item);
                const emailCreatedDateTime = yield getCreatedDate(item);
                yield GraphEmailService.forwardEmailBySearchFallback(graphToken, emailSubject, emailSender, emailCreatedDateTime, placementId, sharedMailbox);
            }
            catch (error) {
                DebugService.error('Search fallback failed');
                const errorMessage = error instanceof Error ? error.message : String(error);
                throw new Error(`Search fallback failed: ${errorMessage}`);
            }
        });
    }
    saveAndGetItemId(item_1) {
        return __awaiter(this, arguments, void 0, function* (item, retry = 0) {
            return new Promise((resolve, reject) => {
                item.saveAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        const exchangeId = result.value;
                        if (exchangeId) {
                            DebugService.debug('Got Exchange ID from saveAsync');
                            // Convert Exchange ID to REST ID for Graph API
                            Office.context.mailbox.convertToRestId([exchangeId], Office.MailboxEnums.RestVersion.v2_0, (convertResult) => {
                                var _a;
                                if (convertResult.status === Office.AsyncResultStatus.Succeeded) {
                                    const restId = (_a = convertResult.value) === null || _a === void 0 ? void 0 : _a[0];
                                    DebugService.debug('Converted Exchange ID to REST ID');
                                    // Add a small delay to ensure itemId is properly set on the item
                                    setTimeout(() => {
                                        resolve(restId || exchangeId);
                                    }, 100);
                                }
                                else {
                                    DebugService.warn('Failed to convert Exchange ID to REST ID, using original');
                                    // Fallback: use original ID if conversion fails
                                    setTimeout(() => {
                                        resolve(exchangeId);
                                    }, 100);
                                }
                            });
                        }
                        else if (retry < 5) {
                            // Enhanced retry with exponential backoff
                            const delay = Math.min(1000 * Math.pow(2, retry), 10000); // Max 10s
                            DebugService.warn(`Item ID was null, retrying in ${delay}ms... (attempt ${retry + 1}/5)`);
                            setTimeout(() => {
                                this.saveAndGetItemId(item, retry + 1).then(resolve).catch(reject);
                            }, delay);
                        }
                        else {
                            DebugService.error('Item ID is still null after 5 retries');
                            reject(new Error('Failed to get itemId after 5 retries'));
                        }
                    }
                    else {
                        DebugService.error('Failed to save draft');
                        reject(result.error);
                    }
                });
            });
        });
    }
    // Method to trigger early save when user interacts with UI
    attemptEarlySave(item) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Only attempt save if we're in compose mode (draft) and don't have itemId
                if (OfficeModeService.isComposeMode() && !item.itemId) {
                    DebugService.debug('Attempting early save on user interaction (draft mode)');
                    yield this.saveAndGetItemId(item);
                    DebugService.debug('Early draft save successful');
                }
            }
            catch (error) {
                DebugService.warn('Early save failed, will retry later');
                // Don't throw - let user continue with their workflow
            }
        });
    }
    processSubmission(apiToken, graphToken, item, 
    //productCode: string,
    lineOfBusiness, subLineOfBusiness, sendCopyToCyberAdmin, assignIngestionToSelf) {
        return __awaiter(this, void 0, void 0, function* () {
            // Step 1: Save draft if not already saved (compose mode only) and get itemId
            let itemId = item.itemId;
            if (OfficeModeService.isComposeMode() && !itemId) {
                try {
                    itemId = yield this.saveAndGetItemId(item);
                    DebugService.debug('Initial draft saved successfully with itemId');
                }
                catch (error) {
                    LoggingService.logError(error, 'Saving draft email');
                    throw error;
                }
            }
            else if (itemId) {
                // Convert existing itemId to REST ID if it hasn't been converted yet
                try {
                    const restId = yield OfficeIdConverterService.convertToRestId(itemId);
                    if (restId !== itemId) {
                        DebugService.debug('Converted existing itemId to REST ID');
                        itemId = restId;
                    }
                }
                catch (error) {
                    DebugService.warn('Failed to convert existing itemId, using original');
                }
            }
            // Step 3: Convert email and submit placement
            DebugService.placement('Converting email and submitting placement');
            const converter = new EmailConverterService();
            const emlData = yield converter.convertEmailToEml(item);
            // Use util helpers for subject, sender, and created date
            const emailSender = yield getSender(item);
            const emailSubject = yield getSubject(item);
            const emailReceivedDateTime = yield getCreatedDate(item);
            const internetMessageId = yield getInternetMessageId(item);
            const mailbox = Office.context.mailbox.userProfile.emailAddress;
            // Ensure all values are strings to prevent .replace() errors
            const validatedEmailSender = String(emailSender || '');
            const validatedEmailSubject = String(emailSubject || '');
            const validatedInternetMessageId = String(internetMessageId || '');
            const validatedMailBox = String(mailbox || '');
            const validatedEmailReceivedDateTime = String(emailReceivedDateTime || new Date().toISOString());
            const validateAssignToMe = String(assignIngestionToSelf);
            const data = {
                lineOfBusiness,
                subLineOfBusiness,
                emailSender: validatedEmailSender,
                emailSubject: validatedEmailSubject,
                emailReceivedDateTime: validatedEmailReceivedDateTime,
                emlContent: emlData.content,
                internetMessageId: validatedInternetMessageId,
                mailbox: validatedMailBox,
                assignToMe: validateAssignToMe,
            };
            // Use the service with provided API token
            const placementData = yield PlacementApiService.submitPlacementRequest(apiToken, data);
            yield LoggingService.logPlacementRequest(placementData.placementId, emailSender);
            // Step 4: Stamp the email with workbench ID BEFORE forwarding
            DebugService.debug('Stamping email with workbench ID before forwarding');
            yield stampEmailWithWorkbenchId(item, placementData.placementId, DebugService);
            // Step 4.5: Show Outlook notification banner with WBID
            DebugService.debug('Showing Outlook notification banner with WBID');
            yield showWorkbenchNotificationBanner(item, placementData.placementId, DebugService);
            // Step 5: Handle email forwarding only if needed
            if (lineOfBusiness === "106000" && sendCopyToCyberAdmin) {
                DebugService.email('Cyber product with forwarding enabled - using provided Graph token');
                // Try to get itemId for forwarding
                let currentItemId = itemId;
                if (!currentItemId && OfficeModeService.isComposeMode()) {
                    DebugService.email('Item ID not available - attempting to save draft first');
                    try {
                        // Use the improved saveAndGetItemId function (draft mode only)
                        currentItemId = yield this.saveAndGetItemId(item);
                        DebugService.email('Draft saved successfully with itemId');
                    }
                    catch (saveError) {
                        DebugService.error('Failed to save draft for forwarding');
                        return {
                            success: true,
                            placementId: placementData.placementId,
                            forwardingFailed: true,
                            forwardingFailedReason: saveError instanceof Error ? saveError.message : 'Failed to save draft',
                            lastPlacementId: placementData.placementId,
                            lastSharedMailbox: this.sharedMailbox
                        };
                    }
                }
                // Forward the email using the provided Graph token
                try {
                    DebugService.email('Forwarding email using Graph token');
                    // Detect if the current email is from a shared mailbox
                    const { isShared, mailboxEmail } = yield detectSharedMailbox(item);
                    DebugService.debug(`Detected mailbox type - isShared: ${isShared}, mailboxEmail: ${mailboxEmail}`);
                    // We should now have a valid itemId for all cases
                    if (currentItemId) {
                        // Get internetMessageId for better fallback handling
                        const { getInternetMessageId } = yield import('../utils/emailHelpers');
                        const internetMessageId = yield getInternetMessageId(item);
                        yield GraphEmailService.forwardEmailWithGraphToken(graphToken, {
                            emailId: currentItemId,
                            uwwbID: placementData.placementId,
                            sharedMailbox: this.sharedMailbox,
                            internetMessageId: internetMessageId || undefined,
                        }, mailboxEmail, isShared);
                    }
                    else {
                        // Fallback: if somehow we still don't have itemId, try conversationId
                        const conversationId = item.conversationId;
                        if (conversationId) {
                            DebugService.debug('Using conversationId as fallback');
                            // Get internetMessageId for better fallback handling
                            const { getInternetMessageId } = yield import('../utils/emailHelpers');
                            const internetMessageId = yield getInternetMessageId(item);
                            yield GraphEmailService.forwardEmailByConversationId(graphToken, conversationId, placementData.placementId, this.sharedMailbox);
                        }
                        else {
                            yield this.searchBySubjectAndDateThenForward(item, graphToken, placementData.placementId, this.sharedMailbox);
                        }
                    }
                    DebugService.email('Email forwarding successful');
                    return {
                        success: true,
                        placementId: placementData.placementId,
                        forwardingFailed: false
                    };
                }
                catch (forwardError) {
                    DebugService.error('Email forwarding failed');
                    return {
                        success: true,
                        placementId: placementData.placementId,
                        forwardingFailed: true,
                        forwardingFailedReason: forwardError instanceof Error ? forwardError.message : 'Forwarding failed',
                        lastPlacementId: placementData.placementId,
                        lastGraphItemId: currentItemId,
                        lastSharedMailbox: this.sharedMailbox
                    };
                }
            }
            // Success without forwarding
            return {
                success: true,
                placementId: placementData.placementId,
                forwardingFailed: false
            };
        });
    }
}
